/**
 * 개선 포인트(사례) AI 이미지 — 등록형 행 위젯 `improvement-image`의 호스트 콜백 계약 + 순수 흐름.
 * (결정 2026-09-14-risk-assessment-report-composed-transition §4 — 선언은 템플릿, 실행은 호스트)
 *
 * rc는 API를 모른다(photo 업로드 `ComposerPhotoUpload`·검색 제안 `ComposerSuggestSearch`와 같은
 * 축). web 작성 페이지가 `ComposerImprovementImageHost`를 만들어 `onImprovementImage` prop으로
 * 넘기면 렌더러가 등록형 위젯 컨텍스트(`registeredWidgetContext` / `rowWidgetContext`)의
 * `improvementImage` 키에 싣는다 — 등록형 행 위젯의 props는 `{value,onChange,editable,context}`뿐이라
 * 개별 prop 관통이 아니라 컨텍스트가 유일한 채널이다. 미주입(캔버스·external 무인증)이면 위젯은
 * 이미지 표시 전용으로 degrade한다.
 *
 * 값 형상(계약 REGISTERED_OBJECT_FIELD_CONTRACTS['improvement-image']와 1:1):
 *   { url: string; prompt?: string; model?: string; imageId?: string; usageCount?: number; fileId?: string }
 *   - 빈 값 = { url: '' }. 본문 image 위젯은 `<필드>.url`을 바인딩한다.
 *   - 새로 생성하면 prompt·model·**fileId**(원장 포인터, P3-8)가 실린다. imageId·usageCount는 **변환된 구 문서**(레거시 RA의 유사 사례
 *     재사용)가 싣고 있는 값이라 계약에 남긴다 — 승인 시 지식 훅이 imageId로 재사용 카운트를 올린다.
 *     이 위젯은 imageId를 새로 만들지 않는다(생성 커밋은 재사용 흔적을 지운다).
 *
 * 유사 사례 검색(2026-09-14 사용자 결정으로 제거): 종전엔 텍스트 임베딩(개선방안 문장 코사인 ≥0.7)으로
 * 유사 이미지를 찾아 첫 후보를 채택했으나, 텍스트 유사도가 이미지 적합성을 보장하지 않아 **항상 새로
 * 생성**한다. 이미지 임베딩이 도입되면 `search` 계약을 다시 붙인다(서버 `POST /ai/improvement-images/search`는
 * 그대로 있다 — rc·web 호스트만 부르지 않는다).
 *
 * 입력(finding)은 **행 객체에서** 만든다 — 어느 키를 읽을지는 서버 조립 행(`ra-items-src`)과 합의된
 * 고정 키(`IMPROVEMENT_IMAGE_FINDING_KEYS`)다. 위젯 props로 필드 매핑을 받지 않는다.
 *
 * 비동기 흐름(runImprovementImageGenerate)은 photo 업로드 `runPhotoUpload` 규약 동형 — rc엔 jsdom이
 * 없어 deferred promise로 단위 검증하고, isAlive로 언마운트(행 삭제·화면 이탈) 뒤 stale 커밋을 차단한다.
 */

/** 위젯 키 — 레지스트리(COMPOSED_ROW_WIDGETS)·계약 사전·admin select가 같은 문자열을 쓴다. */
export const IMPROVEMENT_IMAGE_WIDGET_KEY = 'improvement-image';

/** 행 객체에서 finding을 만들 때 읽는 고정 키(ra-template 조립 행과 합의 — 바꾸지 말 것). */
export const IMPROVEMENT_IMAGE_FINDING_KEYS = [
  'findingId',
  'photoId',
  'photoUrl',
  'riskSummary',
  'improvementPlan',
  'locationText',
  'riskTypes',
] as const;

/** 호스트에 넘기는 발견사항 입력(행 객체에서 파생 — 전부 선택). */
export interface ImprovementImageFinding {
  findingId?: string;
  photoId?: string;
  /** 개선 전 사진 URL — 생성 API의 참조 사진. */
  photoUrl?: string;
  /** 위험요인 요약 — 생성 프롬프트의 description. */
  riskSummary?: string;
  /** 개선대책 — 생성 measures. */
  improvementPlan?: string;
  locationText?: string;
  riskTypes?: string[];
}

/** 새로 생성한 이미지(서버 `POST /ai/generate-improvement-image` 응답에서 추린 것). */
export interface ImprovementImageGenerated {
  url: string;
  prompt?: string;
  model?: string;
  /**
   * 원장 행 id(`files` purpose `IMPROVEMENT_IMAGE`) — 서버가 **생성 시점에** 만든다(P3-8,
   * 결정 2026-10-09-every-r2-object-has-exactly-one-files-row 「집행 시점 확정」). 값 객체에 실려
   * 결재 완료 때 `improvement_images.file_id`가 되고, **그것이 「채택」의 정의**다. rc는 이 값을
   * 읽지도 그리지도 않고 **옮기기만 한다** — 중간 어느 층에서 흘리면 그 사슬이 조용히 끊긴다.
   * optional인 이유: 호스트 미배선·구 응답·변환된 구 문서.
   */
  fileId?: string;
}

/**
 * 호스트 콜백 계약 — web이 companyId를 닫아 넣은 구현을 준다.
 * - generate: 새 이미지 1장. 실패는 reject.
 * `search`(유사 사례)는 두지 않는다 — 2026-09-14 제거, 이미지 임베딩 도입 시 재추가(파일 헤더).
 * `reuse`(재사용 표시)도 두지 않는다 — 재사용 카운트는 승인 시 지식 훅이 imageId로 올린다(결정 §6).
 */
export interface ComposerImprovementImageHost {
  generate: (
    finding: ImprovementImageFinding,
    opts?: { model?: string },
  ) => Promise<ImprovementImageGenerated>;
}

/** 위젯 값 객체(계약 자식 경로와 1:1). */
export interface ImprovementImageValue {
  url: string;
  prompt?: string;
  model?: string;
  imageId?: string;
  usageCount?: number;
  /** 원장 포인터 — `ImprovementImageGenerated.fileId` JSDoc이 정본. */
  fileId?: string;
}

export type ImprovementImageStatus = 'idle' | 'generating' | 'done' | 'error';

/** 빈 값 정본 — 행 시드·지우기가 같은 형상을 쓴다. */
export const EMPTY_IMPROVEMENT_IMAGE: ImprovementImageValue = { url: '' };

/** 상태별 안내 문구 — spec이 계약 텍스트로 못 박는다. */
export const IMPROVEMENT_IMAGE_TEXT = {
  generating: '이미지 생성 중…',
  error: '이미지 생성에 실패했습니다. 다시 시도해 주세요.',
  /** 생성 입력이 비어 호출할 수 없을 때. */
  needInput: '위험요인 또는 개선대책을 먼저 입력해 주세요.',
  /** 호스트 미배선·읽기 전용에서 값이 없을 때. */
  empty: '개선 이미지 없음',
} as const;

/**
 * 저작 표면(admin '위젯 키' select) 안내 — 이 위젯이 **동작하는 조건**의 인과를 저작자에게 드러낸다
 * (riskInjectionHint 선례 — 조건이 코드에만 있으면 「왜 어떤 화면에서만 버튼이 뜨는지」를 재현할 수
 * 없다). 조건: 작성 화면이 `onImprovementImage` 호스트 콜백을 주입해야 생성 버튼이 뜬다.
 * 미주입(admin 캔버스·외부 위임 작성 무인증)은 이미지 표시 전용이다.
 */
export const IMPROVEMENT_IMAGE_HOST_HINT =
  '이 위젯의 「새로 생성·다시 생성」은 작성 화면(web)이 AI 이미지 호스트를 주입할 때만 동작합니다 — ' +
  '캔버스·외부 위임 작성(무인증)에서는 이미지 표시 전용입니다. 값은 {url, prompt, model, imageId, usageCount, fileId} ' +
  '객체이고 본문 image 위젯은 `<필드>.url`을 바인딩합니다. 입력은 같은 행의 riskSummary·improvementPlan·' +
  'locationText·riskTypes·photoUrl·findingId·photoId를 읽습니다. ' +
  '작성 진입 시 개선대책(improvementPlan)이 있고 이미지가 비어 있는 행은 자동으로 새로 생성합니다' +
  '(행마다 1회 — 이미지가 이미 있는 편집 진입에서는 실행하지 않습니다). 「다시 생성」은 이미지를 새로 만들어 ' +
  '덮어씁니다. 유사 사례 검색·재사용은 추후 이미지 임베딩 도입 시 다시 붙습니다(imageId·usageCount는 변환된 ' +
  '구 문서의 재사용 흔적입니다).';

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

/** 불명확한 값을 위젯 값 객체로 정규화(객체가 아니면 빈 값). url이 문자열이 아니면 ''. */
export function asImprovementImageValue(value: unknown): ImprovementImageValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return EMPTY_IMPROVEMENT_IMAGE;
  const v = value as Record<string, unknown>;
  const out: ImprovementImageValue = { url: typeof v.url === 'string' ? v.url : '' };
  if (typeof v.prompt === 'string') out.prompt = v.prompt;
  if (typeof v.model === 'string') out.model = v.model;
  if (typeof v.imageId === 'string') out.imageId = v.imageId;
  if (typeof v.usageCount === 'number') out.usageCount = v.usageCount;
  if (typeof v.fileId === 'string') out.fileId = v.fileId;
  return out;
}

/** 행 객체(반복 행 스코프 values 또는 그리드 row)에서 finding 입력을 만든다 — 고정 키만 읽는다. */
export function findingFromRow(row: unknown): ImprovementImageFinding {
  if (!row || typeof row !== 'object') return {};
  const r = row as Record<string, unknown>;
  const out: ImprovementImageFinding = {};
  const findingId = asString(r.findingId);
  const photoId = asString(r.photoId);
  const photoUrl = asString(r.photoUrl);
  const riskSummary = asString(r.riskSummary);
  const improvementPlan = asString(r.improvementPlan);
  const locationText = asString(r.locationText);
  if (findingId) out.findingId = findingId;
  if (photoId) out.photoId = photoId;
  if (photoUrl) out.photoUrl = photoUrl;
  if (riskSummary) out.riskSummary = riskSummary;
  if (improvementPlan) out.improvementPlan = improvementPlan;
  if (locationText) out.locationText = locationText;
  if (Array.isArray(r.riskTypes)) {
    const types = r.riskTypes.filter((t): t is string => typeof t === 'string' && t.trim() !== '');
    if (types.length > 0) out.riskTypes = types;
  } else if (typeof r.riskTypes === 'string' && r.riskTypes.trim() !== '') {
    // 조립이 배열을 구분자로 평탄화한 경우(DataSourceFieldMap.joinWith) — 콤마 분리로 되살린다.
    out.riskTypes = r.riskTypes.split(',').map((s) => s.trim()).filter((s) => s !== '');
  }
  return out;
}

/** 생성을 부를 수 있는 입력인가 — 위험요인·개선대책 둘 다 비면 서버가 만들 근거가 없다. */
export function hasFindingInput(finding: ImprovementImageFinding): boolean {
  return !!(finding.riskSummary || finding.improvementPlan);
}

/**
 * 등록형 위젯 context에서 호스트 콜백을 꺼낸다 — 렌더러가 `improvementImage` 키에 싣는다
 * (트리: registeredWidgetContext / 그리드: rowWidgetContext — 두 채널 같은 키).
 */
export function extractImprovementImageHost(
  context: unknown,
): ComposerImprovementImageHost | undefined {
  if (!context || typeof context !== 'object') return undefined;
  const host = (context as { improvementImage?: unknown }).improvementImage;
  if (!host || typeof host !== 'object') return undefined;
  const h = host as Partial<ComposerImprovementImageHost>;
  return typeof h.generate === 'function' ? (h as ComposerImprovementImageHost) : undefined;
}

/**
 * 등록형 위젯 context에서 **행 객체**를 꺼낸다 — 브리지(rowWidgetAsFormWidget)와 행 그리드
 * (RowsCollectionEditor)가 `scopeValues`에 싣는다. 브리지의 `values`는 본문 폴백 채널이라(P4d)
 * 행 객체가 아니다.
 */
export function extractRowScope(context: unknown): Record<string, unknown> | undefined {
  if (!context || typeof context !== 'object') return undefined;
  const scope = (context as { scopeValues?: unknown }).scopeValues;
  return scope && typeof scope === 'object' && !Array.isArray(scope)
    ? (scope as Record<string, unknown>)
    : undefined;
}

/** 생성 결과 → 값 객체(생성 축만 — 구 문서의 재사용 흔적 imageId·usageCount는 싣지 않는다). */
export function valueFromGenerated(g: ImprovementImageGenerated): ImprovementImageValue {
  const out: ImprovementImageValue = { url: g.url };
  if (g.prompt) out.prompt = g.prompt;
  if (g.model) out.model = g.model;
  // 원장 포인터는 **생성 축**이다(imageId와 달리 새로 만든 이미지의 것) — 여기서 빠지면
  // 결재 완료가 `improvement_images.file_id`를 채울 재료를 영영 못 받는다.
  if (g.fileId) out.fileId = g.fileId;
  return out;
}

interface FlowSinks {
  isAlive: () => boolean;
  setStatus: (next: ImprovementImageStatus) => void;
  setError: (next: string | null) => void;
  commit: (next: ImprovementImageValue) => void;
}

/**
 * 생성 흐름 정본 — 입력 게이트 → generating → 커밋(done) / 실패(error, 값 불변).
 * 진행 중(busy)이면 무시. 언마운트 뒤(isAlive=false)엔 커밋·상태쓰기 전부 버린다.
 */
export async function runImprovementImageGenerate({
  host,
  finding,
  busy,
  isAlive,
  setStatus,
  setError,
  commit,
}: FlowSinks & {
  host: ComposerImprovementImageHost | undefined;
  finding: ImprovementImageFinding;
  busy: boolean;
}): Promise<void> {
  if (!host || busy) return;
  if (!hasFindingInput(finding)) {
    setError(IMPROVEMENT_IMAGE_TEXT.needInput);
    return;
  }
  setError(null);
  setStatus('generating');
  try {
    const generated = await host.generate(finding);
    if (!isAlive()) return;
    if (!generated?.url) throw new Error('empty');
    commit(valueFromGenerated(generated));
    setStatus('done');
  } catch {
    if (!isAlive()) return;
    setError(IMPROVEMENT_IMAGE_TEXT.error);
    setStatus('error');
  }
}

/**
 * 진입 시 자동 생성 조건(구 RA 작성 페이지 동형 — 항목 로드 직후 `if (item.improvementPlan)
 * searchOrGenerateImage(...)`, 편집 진입은 `improvementImageUrl ? 'done' : 'idle'`로 건너뜀. 2026-09-14부터
 * 유사 검색 없이 **항상 생성**):
 *   편집 가능 + 호스트 배선 + 값 url 비어 있음 + 개선대책(improvementPlan) 있음.
 * riskSummary만 있는 행은 대상이 아니다(구 페이지가 improvementPlan만 봤다 — 수동 버튼은 종전대로
 * hasFindingInput 게이트).
 */
export function shouldAutoRunImprovementImage({
  editable,
  host,
  value,
  finding,
}: {
  editable: boolean;
  host: ComposerImprovementImageHost | undefined;
  value: ImprovementImageValue;
  finding: ImprovementImageFinding;
}): boolean {
  return !!(editable && host && !value.url && finding.improvementPlan);
}

/**
 * 자동 실행 1회 가드 — 조건(shouldAutoRunImprovementImage)이 맞고 `guard.current`가 아직 false일 때만
 * 생성 흐름을 띄우고 guard를 true로 올린다. 위젯이 마운트 effect에서 인스턴스 ref를 guard로 넘겨
 * 재렌더·값 변경에 재발동하지 않게 한다. 행 N개는 각자 발동한다(병렬 — 구 페이지의 for 루프가 await
 * 없이 호출했던 것과 같다).
 * @returns 발동했으면 true(조건 불충족·이미 발동이면 false).
 */
export function runImprovementImageAutoOnce({
  guard,
  editable,
  value,
  host,
  finding,
  isAlive,
  setStatus,
  setError,
  commit,
}: FlowSinks & {
  guard: { current: boolean };
  editable: boolean;
  value: ImprovementImageValue;
  host: ComposerImprovementImageHost | undefined;
  finding: ImprovementImageFinding;
}): boolean {
  if (guard.current) return false;
  if (!shouldAutoRunImprovementImage({ editable, host, value, finding })) return false;
  guard.current = true;
  void runImprovementImageGenerate({
    host,
    finding,
    busy: false,
    isAlive,
    setStatus,
    setError,
    commit,
  });
  return true;
}
